import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { Paragraph } from '../models/Paragraph';

/**
 * Command state for MoveParagraphToSegmentCommand
 * Captures the state needed for undo operations
 */
interface MoveParagraphState {
  sourceSegmentId: string;
  targetSegmentId: string;
  paragraphId: string;
  paragraphData: {
    id: string;
    startTime: number;
    endTime: number;
    elements: any[];
  };
}

/**
 * Command to move a paragraph from one segment to another
 * 
 * This command removes a paragraph from one segment and adds it to another,
 * updating all necessary references and notifying observers.
 */
export class MoveParagraphToSegmentCommand extends AbstractTranscriptEditorCommand {
  /** ID of the source segment */
  private sourceSegmentId: string;
  
  /** ID of the paragraph to move */
  private sourceParagraphId: string;
  
  /** ID of the target segment */
  private targetSegmentId: string;
  
  /** User ID for operation tracking */
  private userId: string;
  
  /** State captured for undo operations */
  private state: MoveParagraphState | null = null;
  
  /**
   * Creates a new MoveParagraphToSegmentCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param sourceSegmentId ID of the segment containing the paragraph
   * @param sourceParagraphId ID of the paragraph to move
   * @param targetSegmentId ID of the segment to move the paragraph to
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    sourceSegmentId: string,
    sourceParagraphId: string,
    targetSegmentId: string,
    userId: string
  ) {
    super(`Move paragraph ${sourceParagraphId} from segment ${sourceSegmentId} to ${targetSegmentId}`, editor);
    
    this.sourceSegmentId = sourceSegmentId;
    this.sourceParagraphId = sourceParagraphId;
    this.targetSegmentId = targetSegmentId;
    this.userId = userId;
    
    // Set this command to require sorting after execution
    this.setRequiresSorting(true);
    
    // Store metadata for debugging and tracking
    this.setMetadata('sourceSegmentId', sourceSegmentId);
    this.setMetadata('sourceParagraphId', sourceParagraphId);
    this.setMetadata('targetSegmentId', targetSegmentId);
    this.setMetadata('userId', userId);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segments
      const sourceSegment = container.getSegment(this.sourceSegmentId);
      const targetSegment = container.getSegment(this.targetSegmentId);
      
      if (!sourceSegment || !targetSegment) {
        return false;
      }
      
      // Get the paragraph
      const sourceParagraph = sourceSegment.getParagraph(this.sourceParagraphId);
      if (!sourceParagraph) {
        return false;
      }
      
      // Store paragraph data for undo
      const paragraphData = {
        id: sourceParagraph.id,
        startTime: sourceParagraph.startTime,
        endTime: sourceParagraph.endTime,
        elements: [...sourceParagraph.elements]
      };
      
      // Save state for undo
      this.state = {
        sourceSegmentId: this.sourceSegmentId,
        targetSegmentId: this.targetSegmentId,
        paragraphId: this.sourceParagraphId,
        paragraphData
      };
      
      // Remove paragraph from source segment
      sourceSegment.removeParagraph(this.sourceParagraphId);
      
      // Update paragraph's segment ID
      sourceParagraph.segmentId = this.targetSegmentId;
      
      // Update all elements to reference the new segment ID
      for (const element of sourceParagraph.elements) {
        element.segmentId = this.targetSegmentId;
      }
      
      // Add paragraph to target segment
      targetSegment.addParagraph(sourceParagraph);
      
      // Record the operation
      this.editor.recordOperation({
        type: 'paragraph_move',
        segmentId: this.sourceSegmentId,
        data: {
          paragraphId: this.sourceParagraphId,
          sourceSegmentId: this.sourceSegmentId,
          targetSegmentId: this.targetSegmentId
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_MOVED, {
        sourceSegment,
        targetSegment,
        paragraph: sourceParagraph,
        sourceSegmentId: this.sourceSegmentId,
        targetSegmentId: this.targetSegmentId
      });
      
      return true;
    } catch (error) {
      console.error('Error executing MoveParagraphToSegmentCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      if (!this.state) {
        console.error('Cannot undo MoveParagraphToSegmentCommand: state is null');
        return false;
      }
      
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segments
      const sourceSegment = container.getSegment(this.sourceSegmentId);
      const targetSegment = container.getSegment(this.targetSegmentId);
      
      if (!sourceSegment || !targetSegment) {
        return false;
      }
      
      // Get the paragraph from the target segment
      const paragraph = targetSegment.getParagraph(this.sourceParagraphId);
      if (!paragraph) {
        // If paragraph not found, recreate it from stored data
        console.warn(`Paragraph ${this.sourceParagraphId} not found in target segment during undo`);
        return false;
      }
      
      // Remove paragraph from target segment
      targetSegment.removeParagraph(this.sourceParagraphId);
      
      // Update paragraph's segment ID back to source
      paragraph.segmentId = this.sourceSegmentId;
      
      // Update all elements to reference the original segment ID
      for (const element of paragraph.elements) {
        element.segmentId = this.sourceSegmentId;
      }
      
      // Add paragraph back to source segment
      sourceSegment.addParagraph(paragraph);
      
      // Notify observers about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_MOVED, {
        sourceSegment: targetSegment, // Reversed for undo
        targetSegment: sourceSegment, // Reversed for undo
        paragraph,
        sourceSegmentId: this.targetSegmentId, // Reversed for undo
        targetSegmentId: this.sourceSegmentId // Reversed for undo
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing MoveParagraphToSegmentCommand:', error);
      
      return false;
    }
  }
}